import { useMemo, useState } from 'react';
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import {
  bytesToKb,
  encodeSpecCell,
  specValueKind,
  type SpecMargins,
  type SpecMediaType,
  type SpecRawValue,
  type SpecValueKind,
} from '@inference.engine/ad-formats/web';
import { specEditor } from '../lib/spec-draft';
import { specRows } from '../lib/spec-rows';
import type { LibraryFormat, LibraryState, SpecValueInput } from '../model/types';
import { requirementLabel, transparencyLabel } from './provenance';
import { SpecValueSheet } from './SpecValueSheet';

interface SpecTabProps {
  state: LibraryState;
  format: LibraryFormat;
  mediaType: SpecMediaType;
  isSaving: boolean;
  onSave: (property: string, input: SpecValueInput | null) => Promise<boolean>;
}

/**
 * Every property the registry lists for one media type. A property with no value says so - "not
 * checked" is information too - and one the format's profile answers shows where that came from.
 */
export function SpecTab({ state, format, mediaType, isSaving, onSave }: SpecTabProps) {
  const { t } = useTranslation('admin');
  const [editing, setEditing] = useState<string | null>(null);
  const rows = useMemo(() => specRows(state, format, mediaType), [state, format, mediaType]);
  const profileName =
    state.profiles.find((profile) => profile.key === format.specProfileKey)?.name ?? '';
  const editingRow = rows.find((row) => row.property === editing);
  const editingKind = editing ? specValueKind(mediaType, editing) : undefined;

  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.adFormats.spec.property')}</TableHead>
              <TableHead>{t('admin.adFormats.spec.value')}</TableHead>
              <TableHead>{t('admin.adFormats.spec.requirement')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(({ property, own, inherited }) => {
              const shown = own ?? inherited;
              const kind = specValueKind(mediaType, property);
              return (
                <TableRow key={property}>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => setEditing(property)}
                      className="font-mono text-body-sm text-primary underline-offset-2 hover:underline"
                    >
                      {property}
                    </button>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    {!shown ? (
                      <span className="text-muted-foreground">
                        {t('admin.adFormats.spec.notChecked')}
                      </span>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2xs">
                        <span className="break-words">
                          {shown.value === null || !kind
                            ? t('admin.adFormats.spec.notApplicable')
                            : displayValue(t, kind, property, shown.value)}
                        </span>
                        {!own && (
                          <Badge variant="secondary">
                            {t('admin.adFormats.spec.inherited', { profile: profileName })}
                          </Badge>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{shown ? requirementLabel(t, shown.requirement) : null}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {editing && editingKind && (
        <SpecValueSheet
          open
          property={editing}
          kind={editingKind}
          current={editingRow?.own}
          canvas={{ w: format.w, h: format.h }}
          isSaving={isSaving}
          onOpenChange={(open) => !open && setEditing(null)}
          onSave={(input) => {
            void onSave(editing, input).then((ok) => ok && setEditing(null));
          }}
        />
      )}
    </>
  );
}

/** A value written the way the sheet takes it, so the table and the editor read the same. */
function displayValue(
  t: ReturnType<typeof useTranslation<'admin'>>['t'],
  kind: SpecValueKind,
  property: string,
  value: SpecRawValue,
): string {
  switch (specEditor(kind, property)) {
    case 'fileSize':
      return t('admin.adFormats.spec.kilobytes', { value: bytesToKb(value as number) });
    case 'transparency':
      return transparencyLabel(t, value as string);
    case 'margins': {
      const { top, right, bottom, left } = value as SpecMargins;
      return t('admin.adFormats.spec.marginsValue', { top, right, bottom, left });
    }
    default:
      return encodeSpecCell(kind, value);
  }
}
